/**
 * Deterministic hashing and pseudo-randomness for generated artwork.
 *
 * Every generator is a pure function of its seed, so the same project always yields
 * byte-identical SVG. That is not cosmetic: the covers are regenerated on every render,
 * and a non-deterministic generator makes them flicker.
 */

/** FNV-1a, 32-bit. Chosen for being tiny and stable, not for cryptographic strength. */
export function hashString(value: string): number {
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);

    hash = Math.imul(hash, 16777619) >>> 0;
  }

  return hash >>> 0;
}

/**
 * A seeded linear congruential generator returning values in [0, 1).
 *
 * Deliberately NOT Math.random(): the artwork must be reproducible from the seed alone.
 * The constants are the Numerical Recipes LCG — adequate for scattering decorative
 * nodes, and it must never be used for anything security-adjacent.
 */
export function createRandom(seed: number): () => number {
  let state = seed >>> 0;

  return () => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    return state / 4294967296;
  };
}

/** Serialises an SVG document to a data URI usable as an `<img>` src. */
export function toDataUri(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}
